import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  EEntityType,
  ERelationType,
  ICorporateDesign,
  IFellowTravelerDto,
  INormalText,
} from '@web/dtos';
import { DateInputComponent } from '../../data-input/form-inputs/date-input/date-input/date-input.component';
import { DropDownComponent } from '../../data-input/form-inputs/drop-down/drop-down.component';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { TextInputComponent } from '../../data-input/form-inputs/text-input/text-input.component';
import { ToTranslatedOptionPipe } from '../../../pipes/to-select-option/to-translated-option.pipe';
import { SalutationStore } from '../../../services/api/salutation/salutation-store.service';
import { FellowTravelerStore } from '../../../services/api/fellow-traveler/fellow-traveler-store.service';
import { ButtonComponent } from '../../feedback/button/button.component';
import { formatDateOnly } from '@web/shared-functions/date';

interface IFellowTravelerItemForm {
  salutation: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  dateOfBirth: FormControl<Date | undefined>;
}

@Component({
  selector: 'aw-account-fellow-traveler-item',
  templateUrl: './account-fellow-traveler-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DateInputComponent,
    ButtonComponent,
    DropDownComponent,
    FilterTextsPipe,
    LanguagePipe,
    ReactiveFormsModule,
    TextInputComponent,
    ToTranslatedOptionPipe,
  ],
})
export class AccountFellowTravelerItemComponent implements OnInit {
  public readonly fellowTraveler: InputSignal<IFellowTravelerDto> =
    input.required<IFellowTravelerDto>();
  public readonly corporateDesign: InputSignal<ICorporateDesign> =
    input.required<ICorporateDesign>();
  public readonly fontColor: InputSignal<string> = input.required<string>();
  public readonly texts: InputSignal<INormalText[]> =
    input.required<INormalText[]>();

  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly salutationStore: SalutationStore = inject(SalutationStore);
  protected readonly fellowTravelerStore: FellowTravelerStore =
    inject(FellowTravelerStore);

  public readonly creationFinished: OutputEmitterRef<void> = output<void>();
  protected fellowTravelerForm!: FormGroup<IFellowTravelerItemForm>;
  protected readonly salutationOptions: Signal<INormalText[]> =
    this.salutationStore.salutationOptions;
  protected readonly isSavingOrDeleting: WritableSignal<boolean> =
    signal(false);
  protected readonly isNewFellowTraveler: Signal<boolean> = computed(
    (): boolean => !this.isExistingFellowTraveler(this.fellowTraveler())
  );
  protected readonly travelerId: Signal<string> = computed((): string => {
    const traveler = this.fellowTraveler();
    return this.isExistingFellowTraveler(traveler) ? traveler.id : 'new';
  });
  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  public ngOnInit(): void {
    const traveler: IFellowTravelerDto = this.fellowTraveler();
    this.fellowTravelerForm = this.formBuilder.group<IFellowTravelerItemForm>({
      salutation: this.formBuilder.nonNullable.control(
        traveler.salutation ?? ''
      ),
      firstName: this.formBuilder.nonNullable.control(traveler.firstName, [
        Validators.required,
      ]),
      lastName: this.formBuilder.nonNullable.control(traveler.lastName, [
        Validators.required,
      ]),
      dateOfBirth: this.formBuilder.nonNullable.control<Date | undefined>(
        this.toDate(traveler.dateOfBirth)
      ),
    });
  }

  protected async save(): Promise<void> {
    if (this.fellowTravelerForm.invalid || this.isSavingOrDeleting()) {
      this.fellowTravelerForm.markAllAsTouched();
      return;
    }

    const value = this.fellowTravelerForm.getRawValue();
    const query: IFellowTravelerDto = {
      salutation: value.salutation || null,
      firstName: value.firstName.trim(),
      lastName: value.lastName.trim(),
      dateOfBirth: value.dateOfBirth ? formatDateOnly(value.dateOfBirth) : null,
    };

    this.isSavingOrDeleting.set(true);
    try {
      const traveler = this.fellowTraveler();
      const isExistingFellowTraveler = this.isExistingFellowTraveler(traveler);
      const savedFellowTraveler: IFellowTravelerDto | undefined =
        isExistingFellowTraveler
          ? await this.fellowTravelerStore.update(traveler.id, query)
          : await this.fellowTravelerStore.create(query);

      if (!savedFellowTraveler) {
        return;
      }
      if (isExistingFellowTraveler) {
        this.resetForm(savedFellowTraveler);
      } else {
        this.creationFinished.emit();
      }
    } finally {
      this.isSavingOrDeleting.set(false);
    }
  }

  protected async delete(): Promise<void> {
    const traveler = this.fellowTraveler();
    if (!this.isExistingFellowTraveler(traveler)) return;

    this.isSavingOrDeleting.set(true);
    try {
      await this.fellowTravelerStore.delete(traveler.id);
    } finally {
      this.isSavingOrDeleting.set(false);
    }
  }

  protected cancel(): void {
    if (this.isNewFellowTraveler()) {
      this.creationFinished.emit();
      return;
    }
    this.resetForm(this.fellowTraveler());
  }

  private resetForm(fellowTraveler: IFellowTravelerDto): void {
    this.fellowTravelerForm.reset({
      salutation: fellowTraveler.salutation ?? '',
      firstName: fellowTraveler.firstName,
      lastName: fellowTraveler.lastName,
      dateOfBirth: this.toDate(fellowTraveler.dateOfBirth),
    });
    this.fellowTravelerForm.markAsPristine();
    this.fellowTravelerForm.markAsUntouched();
  }

  private toDate(value: string | null | undefined): Date | undefined {
    if (!value) return undefined;
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private isExistingFellowTraveler(
    fellowTraveler: IFellowTravelerDto
  ): fellowTraveler is IFellowTravelerDto & { id: string } {
    return !!fellowTraveler.id;
  }
}
